import { useFlag } from '@unleash/proxy-client-react'
import classnames from 'classnames'
import { Formik } from 'formik'
import { CuiButton, CuiIcon, CuiSelect } from 'front-lib'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { canCancelSubscriptionSelector } from 'src/pages/myPlan/components/CancelSubscription/selector'

import Form, { ButtonRow, Col, Grid, Label } from 'src/components/Form'

import {
  FF_PAUSE_DEFLECTION_MODAL,
  FF_PAUSE_INFORMATION_MODAL,
  FF_PAUSE_PLAN_REMOVE_ONE_WEEK,
  FF_CANCEL_SUBSCRIPTION_INTO_MY_ACCOUNT,
  FF_DISABLE_CANCEL_SUBSCRIPTION_BUTTON
} from 'src/constants/feature-flags'

import { useQueryParams } from 'src/hooks/useQueryParams'

import {
  getUpComingOrders,
  getUserCanPause
} from 'src/modules/MyAccount/components/Settings/selectors'
import { getSubscriptionPlanSelector } from 'src/modules/MyAccount/selectors'

import {
  cleanGetLastSkips,
  getLastSkips,
  getUpcomingDays,
  pausePlan,
  unskipOrder
} from 'src/redux-api-bridge'
import { isPrepaidPlanSelector } from 'src/redux-api-bridge/selectors'

import tracking from 'src/shared/tracking'

import Analytics from 'src/utils/analytics'
import { toaster } from 'src/utils/toaster'
import { getDevice, getDeviceOS, isIOSApp } from 'src/utils/utils'

import {
  getDeliveryDay,
  getFirstPausedSkip,
  getPauseResumeDate,
  planIsPaused as planIsPausedSelector
} from '../../selectors'

import { CancelSubscription } from '../CancelSubscription/CancelSubscription'
import FeedbackAfterPausingModal from '../FeedbackAfterPausingModal/FeedbackAfterPausingModal'
import HowCanISkipModal from '../HowCanISkipModal/HowCanISkipModal'
import ModalFeedback from '../ModalFeedback'

import './PausePlanSettings.scss'
import {
  closePauseDeflectionModal,
  openPauseDeflectionModal
} from 'src/redux-api-bridge/pauseDeflectionModal/actions'
import { PauseDeflectionModal } from 'src/components/PauseDeflectionModal/PauseDeflectionModal'
import { CancelSubscriptionWarning } from '../CancelSubscription/CancelSubscriptionWarning'

const defaultPauseOptions = [
  { label: '1 week', value: 1 },
  { label: '2 weeks', value: 2 },
  { label: '3 weeks', value: 3 },
  { label: '4 weeks', value: 4 },
  { label: '5 weeks', value: 5 },
  { label: '6 weeks', value: 6 },
  { label: '7 weeks', value: 7 },
  { label: '8 weeks', value: 8 }
]

export function PausePlanSettings() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const query = useQueryParams()

  const upcomingOrders = useSelector(getUpComingOrders)
  const subscriptionPlan = useSelector(getSubscriptionPlanSelector)
  const planIsPausedInitial = useSelector(planIsPausedSelector)
  const userCanPause = useSelector(getUserCanPause)

  const canCancelSubscription = useSelector(canCancelSubscriptionSelector)
  const errorPausePlan = useSelector<any>(
    state => state?.api?.pausePlanStatus?.error ?? false
  )

  const loadingPausePlan = useSelector<any>(
    state => state?.api?.pausePlanStatus?.inFlight || false
  )

  const deliveriesPerWeek = subscriptionPlan?.deliveries ?? 1
  const mealsPerDelivery = subscriptionPlan?.mealsPerDelivery ?? 1
  const mealsPerWeek = mealsPerDelivery * deliveriesPerWeek

  const [pauseDuration, setPauseDuration] = useState(0)
  const [showFeedbackModal, setShowFeedbackModal] = useState(false)
  const [showHowCanISkipModal, setShowHowCanISkipModal] = useState(false)
  const [showPausePlanSection, setShowPausePlanSection] = useState(false)
  const [planIsPaused, setPlanIsPaused] = useState(planIsPausedInitial)
  const isPrepaidPlan = useSelector(isPrepaidPlanSelector)

  const removeFirstWeekFlag = useFlag(FF_PAUSE_PLAN_REMOVE_ONE_WEEK)
  const enablePauseDeflectionModal = useFlag(FF_PAUSE_DEFLECTION_MODAL)
  const ffDisableCancelSubscriptionButton = useFlag(
    FF_DISABLE_CANCEL_SUBSCRIPTION_BUTTON
  )

  const cancelSubscriptionIntoMyAccount = useFlag(
    FF_CANCEL_SUBSCRIPTION_INTO_MY_ACCOUNT
  )
  const pauseOptions = useMemo(() => {
    if (removeFirstWeekFlag) {
      return defaultPauseOptions.slice(1)
    }

    return defaultPauseOptions
  }, [removeFirstWeekFlag])

  function getNamePlan(mealsPerWeek) {
    return `${mealsPerWeek} ${
      mealsPerWeek > 1 ? t('pages.my_plan.meals') : t('pages.my_plan.meal')
    }`
  }

  function handleCancelPlan() {
    setShowPausePlanSection(false)
  }

  async function handlePausePlan(reason, duration, mealsPerWeek) {
    setPlanIsPaused(true)
    dispatch(closePauseDeflectionModal())

    Analytics.track('Pause-Plan', {
      plan_name: getNamePlan(mealsPerWeek),
      is_paused: true,
      pause_duration: `${duration} Weeks`,
      reason: reason,
      origin: 'my-plan'
    })

    dispatch(pausePlan(duration, reason))
  }

  function handleRequestFeedbackBeforePausePlan(values, { setSubmitting }) {
    const duration = values.duration.value
    if (enablePauseDeflectionModal) {
      dispatch(openPauseDeflectionModal())
    } else {
      setShowFeedbackModal(true)
    }
    setPauseDuration(duration)
    setSubmitting(false)
    setShowPausePlanSection(false)
  }

  async function handlePlanUnpaused() {
    setPlanIsPaused(false)

    tracking.track('Subscription Unpaused', {
      unpause_source: 'My Plan',
      delivery_date: upcomingOrders[0]?.order.delivery_date,
      page_name: 'My Plan'
    })
  }

  function handlePlanUnpausedError() {
    setPlanIsPaused(true)
  }

  function getPauseButton(isSubmitting, isValid) {
    return (
      <CuiButton type="submit" disabled={isSubmitting || !isValid}>
        {t('pages.my_plan.pause')}
      </CuiButton>
    )
  }

  function handleOpenHowCanISkipModal(e) {
    setShowHowCanISkipModal(true)
  }

  function handleCloseHowCanISkipModal() {
    setShowHowCanISkipModal(false)
  }

  useEffect(() => {
    setPlanIsPaused(planIsPausedInitial)
  }, [planIsPausedInitial])

  useEffect(() => {
    dispatch(getUpcomingDays())
  }, [dispatch])

  useEffect(() => {
    if (!showPausePlanSection) {
      return
    }
    if (enablePauseDeflectionModal) {
      tracking.track('Pause Modal', {
        action: 'Open Modal',
        overlay_name: 'Manage Your Order - Pause',
        overlay_class: 'modal',
        overlay_source: 'custom',
        overlay_type: 'button',
        page_name: 'My Plan',
        device: getDevice(),
        device_os: getDeviceOS()
      })
    } else {
      tracking.track('Pause Modal', {
        action: 'Open Modal'
      })
    }
  }, [showPausePlanSection, enablePauseDeflectionModal])

  useEffect(() => {
    // Cancel page has a "pause plan" button whose rely on this page to pause the plan.
    if (query.get('pause-plan') !== null) {
      setShowPausePlanSection(true)
    }

    if (query.get('skip-education') !== null) {
      setShowHowCanISkipModal(true)
    }
  }, [query])

  useEffect(() => {
    if (errorPausePlan) {
      toaster.error(errorPausePlan, {
        icon: <CuiIcon name="close" className="cookunity__pause-error-icon" />,
        position: toast.POSITION.TOP_RIGHT,
        autoClose: true
      })
    }
  }, [errorPausePlan])

  if (planIsPaused) {
    return (
      <UnpausePlanSettings
        onPlanUnpaused={handlePlanUnpaused}
        onPlanUnpausedError={handlePlanUnpausedError}
        loadingPause={loadingPausePlan}
      />
    )
  }

  const showWarningDisableCancelButton =
    ffDisableCancelSubscriptionButton &&
    !canCancelSubscription.result &&
    canCancelSubscription.completed
  const disableButton =
    ffDisableCancelSubscriptionButton && !canCancelSubscription.result

  return (
    <>
      <ModalFeedback
        isOpen={showFeedbackModal}
        onRequestClose={() => setShowFeedbackModal(false)}
        doPause={handlePausePlan}
        pauseDuration={pauseDuration}
        upcomingOrders={upcomingOrders}
        mealsPerWeek={mealsPerWeek}
      />
      <PauseDeflectionModal
        handlePause={handlePausePlan}
        pauseDuration={pauseDuration}
        mealsPerWeek={mealsPerWeek}
      />
      <HowCanISkipModal
        isOpen={showHowCanISkipModal}
        onClose={handleCloseHowCanISkipModal}
      />
      {showPausePlanSection ? (
        <Formik
          initialValues={{
            duration: pauseOptions[0]
          }}
          onSubmit={handleRequestFeedbackBeforePausePlan}>
          {({ isSubmitting, values, setFieldValue, isValid }) => (
            <Form className="">
              <h3 className="subtitle">{t('pages.my_plan.pause_plan')}</h3>
              <Label strong={undefined} inline={undefined} className="">
                {t('pages.my_plan.select_a_duration')}
              </Label>
              <Grid>
                <Col>
                  <CuiSelect
                    value={values.duration}
                    isDisabled={isSubmitting}
                    options={pauseOptions}
                    onChange={selection => {
                      setFieldValue('duration', selection, true)
                    }}
                  />
                </Col>
              </Grid>
              <ButtonRow className="">
                {getPauseButton(isSubmitting, isValid)}
                <CuiButton fill="outline" onClick={handleCancelPlan}>
                  {t('pages.my_plan.cancel')}
                </CuiButton>
              </ButtonRow>
            </Form>
          )}
        </Formik>
      ) : (
        <div className="cookunity__setting-section-container">
          {!isPrepaidPlan && (
            <div className="cookunity__setting-section-text-container">
              <span className="cookunity__setting-section-title">
                {t('pages.my_plan.need_a_break')}{' '}
                <span className="link" onClick={handleOpenHowCanISkipModal}>
                  {t('pages.my_plan.how_can_i_skip.title')}
                </span>
              </span>
              <span className="cookunity__setting-section-subtitle">
                {t('pages.my_plan.traveling')}
              </span>
            </div>
          )}
          {isPrepaidPlan && (
            <div className="cookunity__setting-section-text-container">
              <span className="cookunity__setting-section-subtitle">
                {t('pages.my_plan.prepaid')}
              </span>
            </div>
          )}
          <div className="cookunity__setting-section-buttons-container">
            {!isPrepaidPlan && userCanPause && (
              <button
                className="cookunity__setting-section-buttons-primary"
                onClick={() => setShowPausePlanSection(true)}>
                {t('pages.my_plan.pause_plan_action')}
              </button>
            )}
            {!isIOSApp() && !cancelSubscriptionIntoMyAccount && (
              <CancelSubscription
                pageName={'My Plan'}
                disabled={disableButton}
              />
            )}
          </div>
        </div>
      )}
      <div className={'cookunity__setting-section-cancellation-warning'}>
        {!isIOSApp() &&
          !cancelSubscriptionIntoMyAccount &&
          showWarningDisableCancelButton && <CancelSubscriptionWarning />}
      </div>
    </>
  )
}

function UnpausePlanSettings({
  onPlanUnpaused,
  onPlanUnpausedError,
  loadingPause
}) {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const cancelSubscriptionIntoMyAccount = useFlag(
    FF_CANCEL_SUBSCRIPTION_INTO_MY_ACCOUNT
  )
  const canCancelSubscription = useSelector(canCancelSubscriptionSelector)
  const firstPausedSkip = useSelector(getFirstPausedSkip)
  const pauseResumeDate = useSelector(getPauseResumeDate)
  const deliveryDay = useSelector(getDeliveryDay)

  const loadingUnpausePlan = useSelector<any>(
    state => state?.api?.unskipOrderStatus?.inFlight || false
  )

  const errorUnpausePlan = useSelector<any>(
    state => state?.api?.unskipOrderStatus?.error ?? false
  )

  const ffDisableCancelSubscriptionButton = useFlag(
    FF_DISABLE_CANCEL_SUBSCRIPTION_BUTTON
  )
  const enablePauseInformationModal = useFlag(FF_PAUSE_INFORMATION_MODAL)

  const [
    showFeedbackAfterPausingModal,
    setShowFeedbackAfterPausingModal
  ] = useState(false)

  async function handleUnpausePlan() {
    await dispatch(unskipOrder({ date: firstPausedSkip }))
    onPlanUnpaused()
  }

  function handleFeedbackAfterPausingModal() {
    setShowFeedbackAfterPausingModal(!showFeedbackAfterPausingModal)
  }

  useEffect(() => {
    if (loadingPause) {
      dispatch(cleanGetLastSkips())
      return
    }
    dispatch(getLastSkips({ user: null, fromLastDays: 0 }))
  }, [dispatch, loadingPause])

  useEffect(() => {
    if (errorUnpausePlan) {
      toaster.error(errorUnpausePlan, {
        icon: <CuiIcon name="close" className="cookunity__pause-error-icon" />,
        position: toast.POSITION.TOP_RIGHT,
        autoClose: true
      })
    }

    onPlanUnpausedError()
  }, [errorUnpausePlan, onPlanUnpausedError])

  if (loadingUnpausePlan) {
    return <LoadingPauseSettings />
  }

  const isLoading = loadingPause || !firstPausedSkip || !pauseResumeDate

  const showWarningDisableCancelButton =
    ffDisableCancelSubscriptionButton &&
    !canCancelSubscription.result &&
    canCancelSubscription.completed
  const disableButton =
    ffDisableCancelSubscriptionButton && !canCancelSubscription.result

  return (
    <>
      {!isLoading &&
        enablePauseInformationModal &&
        showFeedbackAfterPausingModal && (
          <FeedbackAfterPausingModal
            onClose={handleFeedbackAfterPausingModal}
          />
        )}
      <div
        className={classnames(
          'cookunity__setting-section-container',
          isLoading && 'loading'
        )}>
        <div className="cookunity__setting-section-text-container">
          <span className="cookunity__plan-paused-section-title">
            {isLoading && <div>&nbsp;</div>}
            {!isLoading && t('pages.my_plan.plan_currently_paused')}
          </span>
          <ul className="cookunity__plan-paused-section-info">
            {isLoading ? (
              <>
                <div>&nbsp;</div>
                <div>&nbsp;</div>
              </>
            ) : (
              <>
                <li>
                  {t('pages.my_plan.plan_scheduled_resume', {
                    date: pauseResumeDate,
                    week_day: deliveryDay
                  })}
                </li>
                <li>{t('pages.my_plan.unpause_plan')}</li>
              </>
            )}
          </ul>
          {enablePauseInformationModal && (
            <span
              className="link check-calendar-pause margin-top"
              onClick={handleFeedbackAfterPausingModal}>
              {t('pages.my_plan.check_calendar_pause')}
            </span>
          )}
        </div>

        <div className="cookunity__setting-section-buttons-container paused">
          <CuiButton
            size="large"
            loading={loadingUnpausePlan || isLoading}
            disabled={loadingUnpausePlan || errorUnpausePlan || isLoading}
            className="cookunity__setting-section-buttons-unpause"
            onClick={handleUnpausePlan}>
            {t('pages.my_plan.unpause_plan_action')}
          </CuiButton>
          {!isIOSApp() && !cancelSubscriptionIntoMyAccount && (
            <CancelSubscription pageName={'My Plan'} disabled={disableButton} />
          )}
        </div>
      </div>
      <div className={'cookunity__setting-section-cancellation-warning'}>
        {!isIOSApp() &&
          !cancelSubscriptionIntoMyAccount &&
          showWarningDisableCancelButton && <CancelSubscriptionWarning />}
      </div>
    </>
  )
}

function LoadingPauseSettings() {
  return (
    <div className="cookunity__setting-section-container loading">
      <div className="cookunity__setting-section-text-container">
        <span className="cookunity__plan-paused-section-title">&nbsp;</span>
        <div className="cookunity__plan-paused-section-info">
          <div>&nbsp;</div>
          <div>&nbsp;</div>
        </div>
      </div>
    </div>
  )
}
